import { useQuery } from '@tanstack/react-query';
import { showResult } from 'infrastructure/hooks/utils';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dataService from 'services/dataService';
import { apiDashboardNextAppointments } from 'services/dataService/resources';
import { getLanguageLabelById } from 'utils';

import {
  buildAppointmentMethodTag,
  buildPatientName,
  formatAppointmentTimeCSTLabel,
  formatAppointmentTimeLabel,
  formatBirthDate,
  toLocalAppointmentDateTime,
} from '../utils/appointment-formatters';
import { getTodayRangeTimestamps } from '../utils/time-range';

import type { INextAppointmentDetails } from 'components/molecules/oneplaform/next-appointment/types';
import type { TFunction } from 'i18next';

const NEXT_APPOINTMENT_QUERY_KEY = 'home-dashboard/next-appointment';

type AppointmentMethodApi =
  | 'phone_or_video_conference'
  | 'sms'
  | 'email'
  | string;

type ScheduledWith = {
  guid: string;
  name?: string;
  type?: string;
};

type PatientInfo = {
  guid: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  birthDate?: string;
  language?: string;
};

type NextAppointmentApiResponse = {
  count?: number;
  page?: number;
  items?: number;
  data?: TodayAppointmentApiItem[];
};

type TodayAppointmentApiItem = {
  guid: string;
  patientGuid: string;
  scheduledDate?: string;
  appointmentMethod?: AppointmentMethodApi;
  scheduledWith?: ScheduledWith;
  patient?: PatientInfo;
};

const fetchNextAppointment = async () => {
  const { now: nextAppointmentDateFrom, to: nextAppointmentDateTo } =
    getTodayRangeTimestamps();

  const endpoint = apiDashboardNextAppointments({
    nextAppointmentDateFrom,
    nextAppointmentDateTo,
    page: 0,
    items: 1,
  });

  const { data, error } = await dataService.getList<NextAppointmentApiResponse>(
    endpoint,
  );

  if (error) {
    throw new Error(error);
  }

  return data?.data?.[0] ?? null;
};

const mapNextAppointmentDetails = (
  appointment: TodayAppointmentApiItem | null,
  t: TFunction,
): INextAppointmentDetails | null => {
  if (isEmpty(appointment)) return null;

  const appointmentTime = appointment.scheduledDate;
  const appointmentDateTime = toLocalAppointmentDateTime(appointmentTime);
  const patientGuid =
    appointment.patient?.guid ?? appointment.patientGuid ?? '';
  const isDoctor = appointment.scheduledWith?.type === 'doctor';

  return {
    id: appointment.guid,
    patient: {
      guid: patientGuid,
      name: buildPatientName(appointment.patient),
      birthDateLabel: formatBirthDate(appointment.patient?.birthDate),
      languageLabel: appointment.patient?.language
        ? getLanguageLabelById(appointment.patient.language)
        : undefined,
    },
    methodTag: buildAppointmentMethodTag(appointment.appointmentMethod, t),
    dateTimeLabel: formatAppointmentTimeLabel(appointmentTime, t) || '--',
    subDateTimeLabel: formatAppointmentTimeCSTLabel(appointmentTime, t) || '',
    appointmentDateTime,
    appointmentMethod: appointment.appointmentMethod ?? undefined,
    agencyDoctorGuid: isDoctor ? undefined : appointment.scheduledWith?.guid,
    doctorGuid: isDoctor ? appointment.scheduledWith?.guid : undefined,
  };
};

export type UseNextAppointmentOptions = {
  t: TFunction;
};

export const useNextAppointment = ({ t }: UseNextAppointmentOptions) => {
  const [refreshToken, setRefreshToken] = useState(0);

  const scheduleRefresh = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
  }, []);

  const queryResult = useQuery({
    queryKey: [NEXT_APPOINTMENT_QUERY_KEY, refreshToken],
    queryFn: fetchNextAppointment,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (queryResult.error) {
      const errorMessage =
        queryResult.error instanceof Error
          ? queryResult.error.message
          : String(queryResult.error);
      showResult(errorMessage);
    }
  }, [queryResult.error]);

  const details = useMemo(
    () => mapNextAppointmentDetails(queryResult.data ?? null, t),
    [queryResult.data, t],
  );

  return {
    details,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    scheduleRefresh,
  };
};
